import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, IconButton,
  Chip, Stack, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Alert, FormControlLabel, Switch, CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import api from '../../services/api.js';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';

const emptyForm = () => ({
  name: '',
  ivrId: '',
  promptCode: '',
  order: '',
  isActive: true,
});

export default function ExamCitiesList() {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/exam-cities');
      setCities(data.data || []);
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'שגיאה בטעינת ערים', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (city) => {
    setEditId(city._id);
    setForm({
      name: city.name || '',
      ivrId: city.ivrId ?? '',
      promptCode: city.promptCode || '',
      order: city.order ?? '',
      isActive: city.isActive !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!String(form.name || '').trim()) {
      setSnackbar({ open: true, message: 'שם העיר חובה', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: String(form.name).trim(),
        promptCode: String(form.promptCode || '').trim(),
        isActive: !!form.isActive,
      };
      if (form.ivrId !== '' && form.ivrId != null) payload.ivrId = Number(form.ivrId);
      if (form.order !== '' && form.order != null) payload.order = Number(form.order);

      if (editId) {
        await api.put(`/exam-cities/${editId}`, payload);
        setSnackbar({ open: true, message: 'העיר עודכנה', severity: 'success' });
      } else {
        await api.post('/exam-cities', payload);
        setSnackbar({ open: true, message: 'העיר נוספה', severity: 'success' });
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'שגיאה', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/exam-cities/${deleteTarget._id}`);
      setSnackbar({ open: true, message: 'העיר נמחקה', severity: 'success' });
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'שגיאה', severity: 'error' });
      setDeleteTarget(null);
    }
  };

  const toggleActive = async (city) => {
    try {
      await api.put(`/exam-cities/${city._id}`, { isActive: !city.isActive });
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'שגיאה', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <AdminPageHeader
        title="ערי מבחן"
        sub="הרשימה משפיעה על פורטל ההרשמות ועל תפריט הטלפון. מספר IVR הוא המספר שמקישים בטלפון."
        primary={{ label: 'הוספת עיר', onClick: openCreate }}
        legacy={(
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" fontWeight={700}>ערי מבחן</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                הוספת עיר
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              הרשימה משפיעה על פורטל ההרשמות ועל תפריט הטלפון. מספר IVR הוא המספר שמקישים בטלפון.
            </Typography>
          </>
        )}
      />

      <Stack spacing={1}>
        {cities.map((city) => (
          <Paper key={city._id} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip label={city.ivrId} size="small" color="primary" variant="outlined" />
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={600}>{city.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                סדר: {city.order}
                {city.promptCode ? ` · קריינות: ${city.promptCode}` : ' · TTS'}
              </Typography>
            </Box>
            <Chip
              label={city.isActive ? 'פעילה' : 'כבויה'}
              size="small"
              color={city.isActive ? 'success' : 'default'}
              onClick={() => toggleActive(city)}
              sx={{ cursor: 'pointer' }}
            />
            <IconButton size="small" onClick={() => openEdit(city)} aria-label="עריכה">
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => setDeleteTarget(city)} aria-label="מחיקה">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Paper>
        ))}
        {!cities.length && (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">אין ערים — לחצו על «הוספת עיר»</Typography>
          </Paper>
        )}
      </Stack>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'עריכת עיר' : 'הוספת עיר'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="שם העיר"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              required
              autoFocus
            />
            <TextField
              label="מספר בטלפון (IVR)"
              type="number"
              value={form.ivrId}
              onChange={(e) => setForm((f) => ({ ...f, ivrId: e.target.value }))}
              fullWidth
              helperText={editId ? '' : 'ריק = מספר הבא אוטומטית'}
            />
            <TextField
              label="קוד קריינות (אופציונלי)"
              value={form.promptCode}
              onChange={(e) => setForm((f) => ({ ...f, promptCode: e.target.value }))}
              fullWidth
              helperText="למשל 045 — בלי קוד יושמע TTS"
            />
            <TextField
              label="סדר תצוגה"
              type="number"
              value={form.order}
              onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.isActive}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                />
              }
              label="פעילה"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>ביטול</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'שומר...' : 'שמירה'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>מחיקת עיר</DialogTitle>
        <DialogContent>
          <Typography>
            למחוק את «{deleteTarget?.name}»? אם יש הרשמות עם מוקד זה — המחיקה תיחסם.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>ביטול</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>מחיקה</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
